import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht } from '@symphenia/shared-types'
import { ExportButton } from '@/components/ui/ExportButton'
import { useAuthStore } from '@/lib/auth.store'
import { useNavigate } from 'react-router-dom'
import { ArrowUpDown, Check, Settings } from 'lucide-react'
import { FilterBar } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { pageWide } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { useTerminology } from '@/lib/useTerminology'
import { completeTask, fetchTaskList, taskListExportRequest } from '../api/projects-phase-d.api'
import { alsTag } from '../project-labels'
import type { ProjectTask } from '../types/phase-d.types'

/**
 * AP-35 Detailblatt 9 4.3 — **Die Nav-Seite „Aufgaben".**
 *
 * Listen-Standard wie jede Liste im Haus: FilterBar, konfigurierbare Spalten,
 * Hilfe-Popover (`docs/pattern-auswertungsmaske.md`).
 *
 * Drei Reiter, und der erste ist der wichtige: **Meine** zeigt, was diesem
 * Benutzer zugewiesen ist — **auch ohne Leserecht am Projekt** (Blatt 13 4.3).
 * Wem etwas zugewiesen wird, muss es abhaken können; das Projekt dahinter
 * bleibt ihm verschlossen, deshalb steht dort nur Nummer, Titel und Fälligkeit.
 *
 * Das Häkchen erledigt **inline** — eine Aufgabe abzuhaken soll nicht bedeuten,
 * erst eine Detailseite zu öffnen.
 */

type Bereich = 'mine' | 'all' | 'done'

const REITER: { key: Bereich; label: string }[] = [
  { key: 'mine', label: 'Meine' },
  { key: 'all', label: 'Alle' },
  { key: 'done', label: 'Erledigt' },
]

const AVAILABLE_FILTER_FIELDS: FilterFieldDef[] = [
  { key: 'search', label: 'Suche', type: 'text', category: 'core', operators: ['contains', 'not_contains'] },
  { key: 'from', label: 'Fällig von', type: 'datetime', category: 'core', operators: ['is'], defaultTime: '00:00' },
  { key: 'to', label: 'Fällig bis', type: 'datetime', category: 'core', operators: ['is'], defaultTime: '23:59' },
  { key: 'unassigned', label: 'ohne Zuständigen', type: 'boolean', category: 'core', operators: ['is'] },
]

const DEFAULT_FILTER_FIELD_KEYS = ['search']

interface ColDef {
  key: string
  label: string
  align?: 'right'
}

const ALLE_SPALTEN: ColDef[] = [
  { key: 'project', label: 'Zugehörigkeit' },
  { key: 'position', label: 'Position' },
  { key: 'assignee', label: 'zuständig' },
  { key: 'dueAt', label: 'fällig' },
  { key: 'remindAt', label: 'Erinnerung' },
  { key: 'state', label: 'Zustand' },
  { key: 'doneAt', label: 'erledigt am' },
]

const COLUMN_LAYOUT_KEY = 'columnbar_layout_project_tasks'

/** Die aktiven Filter als Abfrageparameter — Liste und Export filtern gleich (Phase K). */
function filterParams(filter: Record<string, ActiveFilter>): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  for (const [key, aktiv] of Object.entries(filter)) {
    if (!aktiv.value) continue
    if (key === 'search') params.search = aktiv.value
    if (key === 'from') params.from = aktiv.value.slice(0, 10)
    if (key === 'to') params.to = aktiv.value.slice(0, 10)
    if (key === 'unassigned') params.unassigned = aktiv.value === 'true'
  }
  return params
}
const DEFAULT_COLUMN_KEYS = ['project', 'assignee', 'dueAt', 'state']

const ZUSTAND_TEXT: Record<string, { label: string; class: string }> = {
  OPEN: { label: 'offen', class: 'bg-muted text-muted-foreground' },
  DUE_TODAY: { label: 'heute', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  OVERDUE: { label: 'überfällig', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  DONE: { label: 'erledigt', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
}

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
}

function loadColumnState(): ColumnState {
  const gespeichert = loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null)
  if (gespeichert?.columnKeys?.length) {
    return { columnKeys: gespeichert.columnKeys, columnWidths: gespeichert.columnWidths ?? {} }
  }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {} }
}

export default function ProjectTasksPage() {
  const terms = useTerminology()
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const darfExport = erfuelltRecht('projects.export.run', user?.permissions)
  const navigate = useNavigate()

  const [bereich, setBereich] = useState<Bereich>('mine')
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(loadColumnState)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const dragKeyRef = useRef<string | null>(null)

  const [zeilen, setZeilen] = useState<ProjectTask[]>([])
  const [meta, setMeta] = useState<{ total: number; page: number; totalPages: number } | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const laden = useCallback(
    async (filter: Record<string, ActiveFilter>, seite: number, welcher: Bereich) => {
      setLoading(true)
      setError(null)
      try {
        const params: Record<string, unknown> = { ...filterParams(filter), scope: welcher, page: seite, limit: 50 }
        const antwort = await fetchTaskList(params)
        setZeilen(antwort.data)
        setMeta(antwort.meta)
      } catch (e) {
        setError(apiErrorText(e, 'Die Aufgaben konnten nicht geladen werden.'))
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    void laden(filterValues, page, bereich)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, bereich])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void laden(neu, 1, bereich), 400)
    },
    [laden, bereich],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(COLUMN_LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleDragStart = (key: string) => {
    dragKeyRef.current = key
  }
  const handleDragOver = (e: React.DragEvent, key: string) => {
    e.preventDefault()
    setDragOverKey(key)
  }
  const handleDragEnd = () => {
    dragKeyRef.current = null
    setDragOverKey(null)
  }
  const handleDrop = (zielKey: string) => {
    const quelle = dragKeyRef.current
    if (!quelle || quelle === zielKey) {
      setDragOverKey(null)
      return
    }
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(zielKey)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
    setDragOverKey(null)
  }

  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null)
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    resizeRef.current = { key, startX: e.clientX, startW: th?.getBoundingClientRect().width ?? 120 }
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const breite = Math.max(60, resizeRef.current.startW + (ev.clientX - resizeRef.current.startX))
      updateColState((prev) => ({
        ...prev,
        columnWidths: { ...prev.columnWidths, [resizeRef.current!.key]: breite },
      }))
    }
    const onUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys
    .map((k) => ALLE_SPALTEN.find((c) => c.key === k))
    .filter((c): c is ColDef => c !== undefined)
  const availableToAdd = ALLE_SPALTEN.filter((c) => !colState.columnKeys.includes(c.key))

  const renderCell = (row: ProjectTask, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'project':
        return row.project ? `${row.project.projectNumber} ${row.project.name}` : '—'
      case 'position':
        return row.position ? `${row.position.number} ${row.position.name}` : '—'
      case 'assignee':
        return row.assigneeName ?? 'nicht zugewiesen'
      case 'dueAt':
        return (
          <span className={row.state === 'OVERDUE' ? 'font-medium text-destructive' : undefined}>
            {alsTag(row.dueAt)}
          </span>
        )
      case 'remindAt':
        return alsTag(row.remindAt)
      case 'doneAt':
        return alsTag(row.doneAt)
      case 'state': {
        const z = ZUSTAND_TEXT[row.state] ?? { label: row.state, class: 'bg-muted' }
        return (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${z.class}`}>
            {z.label}
          </span>
        )
      }
      default:
        return null
    }
  }

  const totalCols = 2 + shownColumns.length + (columnConfigMode && availableToAdd.length > 0 ? 1 : 0) + 1

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Aufgaben</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Was an einem {terms.project} offen ist — mit Fälligkeit, Erinnerung und Zuständigem.
          </p>
        </div>
        {darfExport && (
          <ExportButton
            request={(format, orientation) =>
              taskListExportRequest(
                { ...filterParams(filterValues), scope: bereich, format, orientation },
                shownColumns.map((c) => ({ key: c.key, label: c.key === 'project' ? terms.project : c.label })),
              )
            }
            mail={{ source: 'EXPORT_PROJECT_TASKS', title: t('mailExport.titles.EXPORT_PROJECT_TASKS'), subject: t('mailExport.titles.EXPORT_PROJECT_TASKS') }}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {REITER.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => {
              setBereich(r.key)
              setPage(1)
            }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${
              bereich === r.key
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <FilterBar
        availableFields={AVAILABLE_FILTER_FIELDS}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="project-tasks"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
      />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title="Spalten konfigurieren"
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? {
                  columnKeys: DEFAULT_COLUMN_KEYS,
                  columnWidths: {},
                }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColState(next)
              }}
            />
          </div>
        )}

        {error && (
          <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="w-8" />
                <th
                  className="relative px-4 py-1.5 text-left font-medium text-foreground"
                  style={colState.columnWidths['title'] ? { width: colState.columnWidths['title'] } : undefined}
                >
                  <span className="flex items-center gap-1">
                    Titel
                    <ArrowUpDown className="h-3 w-3 opacity-30" />
                  </span>
                  <div
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                    onMouseDown={(e) => handleResizeStart(e, 'title')}
                  />
                </th>

                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground
                      ${col.align === 'right' ? 'text-right' : 'text-left'}
                      ${columnConfigMode ? 'cursor-grab' : ''}
                      ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className="flex items-center gap-1">
                      <span>{col.label}</span>
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() =>
                            updateColState((prev) => ({
                              ...prev,
                              columnKeys: prev.columnKeys.filter((k) => k !== col.key),
                            }))
                          }
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          title="Spalte entfernen"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                    />
                  </th>
                ))}

                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}

                <th className="w-4" />
              </tr>
            </thead>

            <tbody>
              {loading && zeilen.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Laden…
                  </td>
                </tr>
              )}
              {!loading && zeilen.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Keine Aufgaben gefunden.
                  </td>
                </tr>
              )}

              {zeilen.map((row, idx) => (
                <tr
                  key={row.id}
                  className={`border-b border-border last:border-0 ${
                    idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  } transition-colors hover:bg-accent/30`}
                >
                  <td className="px-2 py-1.5">
                    {!row.doneAt && (
                      <input
                        type="checkbox"
                        aria-label={`${row.title} erledigen`}
                        onChange={async () => {
                          try {
                            await completeTask(row.id)
                            await laden(filterValues, page, bereich)
                          } catch (e) {
                            setError(apiErrorText(e, 'Das hat nicht geklappt.'))
                          }
                        }}
                      />
                    )}
                  </td>
                  <td
                    className="cursor-pointer px-4 py-1.5"
                    onClick={() => row.project && navigate(`/projects/${row.project.id}`)}
                  >
                    {row.title}
                  </td>
                  {shownColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-1.5 whitespace-nowrap ${
                        col.align === 'right' ? 'text-right tabular-nums' : ''
                      }`}
                    >
                      {renderCell(row, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}

              {zeilen.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td />
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                    {loading ? 'Laden…' : `${meta?.total ?? zeilen.length} Aufgaben`}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className="px-4 py-1.5" />
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>
              Seite {meta.page} von {meta.totalPages} ({meta.total} Einträge)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ‹
              </button>
              <button
                type="button"
                disabled={page >= meta.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
